/**
 * Site-wide music player state. Mounted once in App.js so playback survives
 * route changes. Owns the single <audio> element, the HOARD queue (every
 * approved track minus the ones a listener dropped with −), shuffle (on by
 * default, no repeats until every track has played), Sample mode (any length, 22 s to start),
 * the user's ♥ playlist (= download list), and the access level
 * (anonymous → 30s preview · member → full stream · passport → download).
 * Songs start from their starred start point (then the others in turn); family pages only play green songs.
 * Curators (admins + vetted magicians) can also draw at random from the sampler's repositories ("reel" mode).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { API, useAuth } from "../App";
import { ax, cueStarts, normPath, REEL_DEFAULT, DEFAULT_SAMPLE } from "./sampler";
import { grantFor, rawGrant, consumeGrant } from "./shareGrants";
import { readRecent, pushRecent } from "./recentStore";
import { logListen } from "../components/vault/ListenLog";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

export const PREVIEW_SECONDS = 30;
export const SAMPLE_SECONDS = DEFAULT_SAMPLE;
export const ANCHOR_TITLE = "never give up"; // the first song every new player session starts with

const RESUME_KEY = "wall_player_resume";
const REMOVED_KEY = "wall_player_removed";
const absSrc = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const EMPTY_LIB = { tracks: [], access: "anonymous", playlist: [], daily_limit: 22, downloads_today: 0, downloads_disabled: false, licensing_note: "", playlist_note: "", marks: {}, audiences: {} };
const readRemoved = () => { try { return new Set(JSON.parse(localStorage.getItem(REMOVED_KEY) || "[]")); } catch { return new Set(); } };
// A sampler item (any repository) in the player's track shape.
const reelTrack = (it) => ({ item_id: it.key, skey: it.key, reel: true, file_url: it.url, title: it.title, artist_name: it.artist, album: it.sub, src_name: it.src_name,
  source_id: it.source_id, color: it.color, cues: it.cues || [], crates: it.crates || [], binned: !!it.binned, length_sec: it.length_sec, stream_only: true,
  album_art_url: it.show_id ? `https://archive.org/services/img/${it.show_id}` : null });

export const PlayerProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const audioRef = useRef(null);
  const [lib, setLib] = useState(EMPTY_LIB);
  const [libLoaded, setLibLoaded] = useState(false);
  const [removed, setRemoved] = useState(readRemoved);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [shuffle, setShuffle] = useState(true);
  const [sample, setSample] = useState(false);
  const [sampleSeconds, setSampleSeconds] = useState(SAMPLE_SECONDS);
  const [minimized, setMinimized] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  const [recentIds, setRecentIds] = useState(readRecent); // Kwiki · Recently played
  const [kwiki, setKwiki] = useState(false); // Kwiki panel open (from the collapsed tile)
  const [artistArt, setArtistArt] = useState({}); // slug → [urls] (Now-Playing art uploaded by artists)
  const [grant, setGrant] = useState(null); // 3-Play Share grant for the current track (anonymous listeners)
  const grantActive = !!grant;
  const [popKey, setPopKey] = useState(0);
  const playedRef = useRef(new Set());
  const historyRef = useRef([]);
  const [curate, setCurate] = useState(false); // admin, or a magician an admin vetted for the sampler
  const [reel, setReel] = useState(null); // {source, crate} while drawing at random from the sampler
  const reelSeen = useRef([]);
  const drawingRef = useRef(false);
  const cueTurnRef = useRef({}); // item_id → how many times it has started (rotates through its start points)
  const pendingCueRef = useRef(null);

  const marks = useMemo(() => lib.marks || {}, [lib.marks]);
  const familyOnly = (lib.audiences || {})[normPath(location.pathname)] === "family";
  const queue = useMemo(() => lib.tracks.filter((t) => !removed.has(t.item_id) && (!familyOnly || (lib.marks || {})[t.item_id]?.c === "green")), [lib.tracks, lib.marks, removed, familyOnly]);
  const [anchorOverride, setAnchorOverride] = useState(null);
  // Band pages set a focus artist: their songs play first, then the rest of the HOARD at random.
  const [focusArtist, setFocusArtist] = useState(null);
  const slugOf = (t) => t.artist_slug || (t.artist_name || "").toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const focusTracks = useMemo(() => (focusArtist ? queue.filter((t) => slugOf(t) === focusArtist) : []), [queue, focusArtist]);
  const anchor = useMemo(() => (anchorOverride && queue.find((t) => t.item_id === anchorOverride.item_id)) || focusTracks[0] || queue.find((t) => (t.title || "").trim().toLowerCase() === ANCHOR_TITLE) || queue[0] || null, [queue, anchorOverride, focusTracks]);
  const nowArt = useMemo(() => {
    if (!current) return null;
    const imgs = artistArt[slugOf(current)] || [];
    if (!imgs.length) return null;
    const h = [...(current.item_id || "")].reduce((a, c) => a + c.charCodeAt(0), 0);
    return imgs[h % imgs.length];
  }, [current, artistArt]); // eslint-disable-line react-hooks/exhaustive-deps
  const stateRef = useRef({});
  stateRef.current = { queue, current, shuffle, sample, sampleSeconds, access: lib.access, anchor, focusTracks, marks, reel };

  const loadLibrary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hoard/music`, { withCredentials: true });
      setLib({ ...EMPTY_LIB, ...r.data });
    } catch { /* keep previous */ }
    setLibLoaded(true);
  }, []);

  useEffect(() => { loadLibrary(); }, [loadLibrary, user?.user_id]);
  useEffect(() => {
    if (!["admin", "magician"].includes(user?.role)) { setCurate(false); return; }
    axios.get(`${API}/sampler/me`, ax).then(() => setCurate(true)).catch(() => setCurate(false));
  }, [user?.user_id, user?.role]);
  useEffect(() => { window.addEventListener("wall:music-changed", loadLibrary); return () => window.removeEventListener("wall:music-changed", loadLibrary); }, [loadLibrary]);
  useEffect(() => { axios.get(`${API}/artist-art`).then((r) => setArtistArt(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem(REMOVED_KEY, JSON.stringify([...removed])); }, [removed]);
  // Ducking: while Range Free Radio speaks, music fades to the admin-set level (default 20%) and fades back after.
  const duckRef = useRef({ level: 0.2, on: false, timer: null });
  useEffect(() => { axios.get(`${API}/radio/settings`).then((r) => { duckRef.current.level = Number(r.data?.duck_volume ?? 0.08); }).catch(() => {}); }, []);
  const applyVolume = useCallback((target) => {
    const a = audioRef.current; if (!a) return;
    clearInterval(duckRef.current.timer);
    duckRef.current.timer = setInterval(() => {
      const d = target - a.volume;
      if (Math.abs(d) < 0.03) { a.volume = target; clearInterval(duckRef.current.timer); return; }
      a.volume = Math.max(0, Math.min(1, a.volume + Math.sign(d) * 0.06));
    }, 40);
  }, []);
  useEffect(() => { applyVolume(duckRef.current.on ? volume * duckRef.current.level : volume); }, [volume, applyVolume]);
  useEffect(() => {
    const onVoice = (e) => { duckRef.current.on = !!e.detail?.on; applyVolume(duckRef.current.on ? volume * duckRef.current.level : volume); };
    const onLevel = (e) => { duckRef.current.level = Number(e.detail?.level ?? duckRef.current.level); if (duckRef.current.on) applyVolume(volume * duckRef.current.level); };
    const onPause = () => { const a = audioRef.current; if (a && !a.paused) { a.pause(); setPlaying(false); } };
    window.addEventListener("wall:voice", onVoice); window.addEventListener("wall:duck-level", onLevel); window.addEventListener("wall:pause-music", onPause);
    return () => { window.removeEventListener("wall:voice", onVoice); window.removeEventListener("wall:duck-level", onLevel); window.removeEventListener("wall:pause-music", onPause); };
  }, [volume, applyVolume]);
  useEffect(() => { window.__wallMusicPlaying = playing; }, [playing]);

  // Survive a hard reload: restore the last track (paused — browsers block autoplay).
  useEffect(() => {
    if (!libLoaded || current || !lib.tracks.length) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(RESUME_KEY) || "null");
      const t = saved?.current && lib.tracks.find((x) => x.item_id === saved.current);
      if (!t) return;
      setCurrent(t);
      setMinimized(!!saved.minimized);
      if (lib.access === "anonymous") {
        setGrant(rawGrant(t.item_id)); // finish the shared play that was already counted
        previewStartRef.current = saved.previewStart || saved.time || 0; // avoid immediate skip on resume
      }
      const a = audioRef.current;
      if (a) { a.src = absSrc(t.file_url); a.currentTime = saved.time || 0; }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libLoaded]);

  useEffect(() => {
    if (!current) return;
    sessionStorage.setItem(RESUME_KEY, JSON.stringify({ current: current.item_id, time: Math.floor(time), minimized, previewStart: previewStartRef.current || 0 }));
  }, [current, minimized, Math.floor(time / 5)]); // eslint-disable-line react-hooks/exhaustive-deps

  const limitFor = useCallback(() => {
    const { sample: s, sampleSeconds: ss, access, current: cur } = stateRef.current;
    if (cur?.volt && !s) return Infinity; // VÖLT tapes stream in full for everyone (LMA: free, non-commercial)
    if (s) return ss === Infinity && access === "anonymous" ? PREVIEW_SECONDS : ss;
    if (access === "anonymous") return grantActive ? Infinity : PREVIEW_SECONDS;
    return Infinity;
  }, [grantActive]);

  const pickNext = useCallback((dir = 1) => {
    const { queue: q, current: c, shuffle: sh, sample: s, focusTracks: ft } = stateRef.current;
    if (!q.length) return null;
    const ownNext = (ft || []).find((t) => !playedRef.current.has(t.item_id) && t.item_id !== c?.item_id);
    if (ownNext && dir === 1) return ownNext;
    if (sh || s) {
      // Low-scoring VÖLT tapes never come up in shuffle — they only play when someone picks them.
      const eligible = q.filter((t) => t.shuffle_ok !== false);
      const base = eligible.length ? eligible : q;
      let pool = base.filter((t) => !playedRef.current.has(t.item_id) && t.item_id !== c?.item_id);
      if (!pool.length) { playedRef.current = new Set(c ? [c.item_id] : []); pool = base.filter((t) => t.item_id !== c?.item_id); }
      if (!pool.length) return q[0];
      // Random mode never plays the same artist back-to-back when anyone else is still waiting.
      const artistOf = (t) => (t?.artist_id || t?.artist_name || "").toString().toLowerCase();
      const others = c ? pool.filter((t) => artistOf(t) !== artistOf(c)) : pool;
      if (others.length) pool = others;
      return pool[Math.floor(Math.random() * pool.length)];
    }
    const i = q.findIndex((t) => t.item_id === c?.item_id);
    return q[(i + dir + q.length) % q.length];
  }, []);

  // Anonymous preview: pick a random start offset (<2min into the song) once metadata loads.
  const pendingRandomSeek = useRef(false);
  const previewStartRef = useRef(0); // the time offset where the anonymous 30s clip starts
  const applyRandomSeek = useCallback(() => {
    const a = audioRef.current;
    if (!a || !a.duration || a.duration < 35) { previewStartRef.current = 0; return; }
    const maxStart = Math.min(120, Math.max(0, a.duration - 35)); // at least 35s of runway
    if (maxStart <= 0) { previewStartRef.current = 0; return; }
    const offset = Math.floor(Math.random() * maxStart);
    previewStartRef.current = offset;
    a.currentTime = offset;
    setTime(offset);
  }, []);

  const startTrack = useCallback((track, { fromHistory = false } = {}) => {
    if (!track) return;
    const a = audioRef.current;
    const c = stateRef.current.current;
    if (c?.volt && a && c.item_id !== track.item_id) {
      // Anonymous listener signal for VÖLT tapes: a full play or an early skip (nothing about the listener is sent).
      const ev = a.ended || (a.duration && a.currentTime >= a.duration * 0.9) ? "complete" : a.currentTime > 3 ? "skip" : null;
      if (ev) axios.post(`${API}/volt/signal`, { item_id: c.item_id, event: ev, at: Math.floor(a.currentTime || 0), duration: Math.floor(a.duration || 0) }).catch(() => {});
    }
    if (stateRef.current.access !== "anonymous" && !track.reel) logListen({ kind: "played", item_id: track.item_id, artist: track.artist_name || track.artist || "", album: track.album || "", title: track.title || "" });
    if (!fromHistory && c && c.item_id !== track.item_id) historyRef.current = [...historyRef.current.slice(-49), c];
    setCurrent(track);
    setLimitHit(false);
    setTime(0);
    playedRef.current.add(track.item_id);
    if (!track.reel) setRecentIds(pushRecent(track.item_id));
    let hasGrant = false;
    if (stateRef.current.access === "anonymous" && grantFor(track.item_id)) {
      const g = consumeGrant(track.item_id);
      setGrant(g);
      hasGrant = true;
      axios.post(`${API}/hoard/share/${g.share_id}/event`, { type: "play" }).catch(() => {});
    } else setGrant(null);
    // Start points: the starred favourite first, then the others in turn on later plays.
    const starts = track.reel ? cueStarts(track.cues) : (stateRef.current.marks[track.item_id]?.s || []);
    const turn = cueTurnRef.current[track.item_id] || 0;
    cueTurnRef.current[track.item_id] = turn + 1;
    pendingCueRef.current = starts.length ? starts[turn % starts.length] : null;
    // For anonymous preview with no start point: flag a random seek once metadata loads (skip for grants and VÖLT)
    pendingRandomSeek.current = pendingCueRef.current == null && stateRef.current.access === "anonymous" && !hasGrant && !track.volt;
    previewStartRef.current = 0; // reset until metadata loads and random seek fires
    if (a) {
      a.src = absSrc(track.file_url);
      a.currentTime = 0;
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [applyRandomSeek]);

  // Play a specific track (then random through the rest of the HOARD), or start fresh with the anchor song.
  const play = useCallback((track, { keepMinimized = false } = {}) => {
    const { anchor: an, current: c } = stateRef.current;
    if (!keepMinimized) setMinimized(false);
    if (!c) setPopKey((k) => k + 1);
    if (!track && c) { audioRef.current?.play().then(() => setPlaying(true)).catch(() => {}); return; }
    if (!track?.reel) setReel(null);
    const t = track || an;
    if (!t) { if (familyOnly) toast("No family-friendly songs are labelled for this page yet."); return; }
    if (!track) playedRef.current = new Set();
    startTrack(t);
  }, [startTrack, familyOnly]);

  // Curators: draw a random song from the sampler (default: the Aadam Jacobs collection + imported repositories).
  const drawReel = useCallback(async (filters) => {
    if (drawingRef.current) return false;
    drawingRef.current = true;
    const f = filters || stateRef.current.reel || REEL_DEFAULT;
    try {
      const { data } = await axios.get(`${API}/sampler/random`, { ...ax, params: { ...f, avoid: reelSeen.current.slice(-40).join(",") } });
      reelSeen.current = [...reelSeen.current.slice(-199), data.key];
      setReel(f);
      setMinimized(false);
      if (!stateRef.current.current) setPopKey((k) => k + 1);
      startTrack(reelTrack(data));
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing to draw from there yet"); return false; }
    finally { drawingRef.current = false; }
  }, [startTrack]);
  const playReelItem = useCallback((it, filters) => {
    setReel(filters || stateRef.current.reel || REEL_DEFAULT);
    setMinimized(false);
    if (!stateRef.current.current) setPopKey((k) => k + 1);
    startTrack(reelTrack(it));
  }, [startTrack]);
  // A curator's colour / start points / bin change, reflected in the player right away.
  const applyMark = useCallback((track, m) => {
    if (track.reel) { setCurrent((c) => (c && c.item_id === track.item_id ? { ...c, color: m.color, cues: m.cues, crates: m.crates, binned: m.binned } : c)); return; }
    const gone = m.binned || m.color === "red"; // binned / red songs leave the public queue straight away
    if (gone) stateRef.current.queue = stateRef.current.queue.filter((t) => t.item_id !== track.item_id);
    setLib((l) => ({ ...l, tracks: gone ? l.tracks.filter((t) => t.item_id !== track.item_id) : l.tracks, marks: { ...(l.marks || {}), [track.item_id]: { c: m.color, s: cueStarts(m.cues) } } }));
  }, []);

  // "ON AIR" pill → start the music player (Dolly)
  useEffect(() => {
    const h = () => play();
    window.addEventListener("wall:play-music", h);
    return () => window.removeEventListener("wall:play-music", h);
  }, [play]);

  const next = useCallback(() => { if (stateRef.current.reel) { drawReel(); return; } startTrack(pickNext(1)); }, [pickNext, startTrack, drawReel]);
  const exitReel = useCallback(() => { setReel(null); stateRef.current.reel = null; startTrack(pickNext(1)); }, [pickNext, startTrack]);
  const prev = useCallback(() => {
    const a = audioRef.current;
    const offset = previewStartRef.current || 0;
    if (a && (a.currentTime - offset) > 4) { a.currentTime = offset; setTime(offset); return; }
    const last = historyRef.current.pop();
    if (last) { startTrack(last, { fromHistory: true }); return; }
    startTrack(pickNext(-1));
  }, [pickNext, startTrack]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (!current) { play(); return; }
    if (playing) { a.pause(); setPlaying(false); }
    else {
      if (limitHit) { a.currentTime = previewStartRef.current || 0; setLimitHit(false); }
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [current, playing, limitHit, play]);

  const seek = useCallback((t) => {
    const a = audioRef.current;
    if (!a) return;
    const lim = limitFor();
    if (lim === Infinity) { a.currentTime = Math.max(0, t); setTime(a.currentTime); return; }
    const offset = previewStartRef.current || 0;
    // Clips (previews / samples) stay inside their window: offset → offset + limit
    a.currentTime = Math.max(offset, Math.min(t, offset + lim));
    setTime(a.currentTime);
  }, [limitFor]);
  const rewind = useCallback((secs = 10) => { const a = audioRef.current; if (a) seek(a.currentTime - secs); }, [seek]);

  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setTime(a.currentTime);
    const lim = limitFor();
    // For anonymous random-seek previews, measure elapsed time from the clip start, not absolute position
    const elapsed = a.currentTime - (previewStartRef.current || 0);
    if (elapsed >= lim) {
      if (stateRef.current.sample) { next(); return; }
      // Anonymous 30s preview: auto-advance to the next track (don't stop dead)
      if (stateRef.current.access === "anonymous" && !grantActive) { next(); return; }
      a.pause(); setPlaying(false); setLimitHit(true);
    }
  };

  const onEnded = () => {
    const { queue: q, shuffle: sh, sample: s, current: c, reel: rl } = stateRef.current;
    if (rl) { next(); return; }
    const i = q.findIndex((t) => t.item_id === c?.item_id);
    if (!sh && !s && i === q.length - 1) { setPlaying(false); return; }
    next();
  };

  // Called by artist pages on mount (slug) / unmount (null).
  const focusOnArtist = useCallback((slug) => setFocusArtist(slug || null), []);

  const toggleShuffle = useCallback(() => setShuffle((v) => { if (!v) playedRef.current = new Set(current ? [current.item_id] : []); return !v; }), [current]);
  const toggleSample = useCallback(() => {
    setSample((v) => {
      const on = !v;
      if (on) {
        playedRef.current = new Set(current ? [current.item_id] : []);
        if (!current) play();
        else if (audioRef.current && audioRef.current.currentTime - (previewStartRef.current || 0) >= stateRef.current.sampleSeconds) next();
      }
      return on;
    });
  }, [current, play, next]);

  // − / + : drop a track from (or bring it back into) this listener's player. Separate from the ♥ download list.
  const isRemoved = useCallback((id) => removed.has(id), [removed]);
  const toggleRemoved = useCallback((track) => {
    setRemoved((prevSet) => {
      const s = new Set(prevSet);
      if (s.has(track.item_id)) { s.delete(track.item_id); toast.success(`"${track.title}" is back in your player`); }
      else { s.add(track.item_id); toast(`"${track.title}" removed from your player`, { description: "Tap + to bring it back any time." }); }
      return s;
    });
    if (current?.item_id === track.item_id && !removed.has(track.item_id)) next();
  }, [current, removed, next]);

  const isLiked = useCallback((id) => lib.playlist.includes(id), [lib.playlist]);
  const toggleLike = useCallback(async (track) => {
    if (!user) { toast.error("Sign in to build your playlist."); return; }
    try {
      const r = await axios.post(`${API}/hoard/playlist/toggle`, { item_id: track.item_id }, { withCredentials: true });
      setLib((l) => ({ ...l, playlist: r.data.playlist }));
      toast.success(r.data.liked ? `Added "${track.title}" to Your Playlist` : `Removed "${track.title}" from Your Playlist`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update playlist"); }
  }, [user]);

  const download = useCallback(async (track) => {
    if (lib.access !== "passport") { toast.error("Downloads open once your HOARD is unlocked — streaming is always free."); return false; }
    if (!isLiked(track.item_id)) { toast.error(lib.playlist_note || "♥ the track first — downloads come from Your Playlist."); return false; }
    try {
      const r = await axios.get(`${API}/hoard/download/${track.item_id}`, { withCredentials: true, responseType: "blob" });
      saveBlob(r.data, `${track.artist_name} - ${track.title}.mp3`);
      setLib((l) => ({ ...l, downloads_today: l.downloads_today + 1, tracks: l.tracks.map((t) => t.item_id === track.item_id ? { ...t, download_count: (t.download_count || 0) + 1 } : t) }));
      return true;
    } catch (e) { toast.error(await blobError(e)); return false; }
  }, [lib.access, lib.playlist_note, isLiked]);

  const downloadPlaylistZip = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hoard/playlist.zip`, { withCredentials: true, responseType: "blob" });
      saveBlob(r.data, "THE WALL HOARD playlist.zip");
      loadLibrary();
      return true;
    } catch (e) { toast.error(await blobError(e)); return false; }
  }, [loadLibrary]);

  const value = useMemo(() => ({
    lib, libLoaded, reloadLibrary: loadLibrary,
    curate, reel, drawReel, playReelItem, exitReel, applyMark, rewind, familyOnly,
    trackColor: (t) => (t?.reel ? t.color : marks[t?.item_id]?.c) || null,
    trackCues: (t) => (t?.reel ? t.cues || [] : (marks[t?.item_id]?.s || []).map((at, i) => ({ at, starred: i === 0 }))),
    queue, anchor, setAnchorOverride, current, playing, time, duration, volume, shuffle, sample, sampleSeconds, setSampleSeconds, minimized, limitHit, popKey, grant, nowArt, recentIds, kwiki, setKwiki,
    limitSeconds: limitFor(),
    previewStart: previewStartRef.current,
    play, toggle, next, prev, seek, setVolume, toggleShuffle, toggleSample, setMinimized,
    isRemoved, toggleRemoved, isLiked, toggleLike, download, downloadPlaylistZip,
    focusArtist, focusTracks, focusOnArtist,
    stop: () => { const a = audioRef.current; if (a) { a.pause(); a.removeAttribute("src"); a.load(); } setPlaying(false); setCurrent(null); sessionStorage.removeItem(RESUME_KEY); previewStartRef.current = 0; },
  }), [lib, libLoaded, loadLibrary, curate, reel, drawReel, playReelItem, exitReel, applyMark, rewind, familyOnly, marks, queue, anchor, current, playing, time, duration, volume, shuffle, sample, sampleSeconds, minimized, limitHit, popKey, grant, nowArt, recentIds, kwiki, limitFor, play, toggle, next, prev, seek, toggleShuffle, toggleSample, isRemoved, toggleRemoved, isLiked, toggleLike, download, downloadPlaylistZip, focusArtist, focusTracks, focusOnArtist]);

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={onTime}
        onLoadedMetadata={() => {
          const a = audioRef.current;
          setDuration(a?.duration || 0);
          // Start point (starred first): the clip / song starts here
          if (pendingCueRef.current != null && a) {
            const d = a.duration;
            const at = isFinite(d) && d > 2 ? Math.min(pendingCueRef.current, d - 2) : pendingCueRef.current;
            pendingCueRef.current = null;
            previewStartRef.current = at;
            a.currentTime = at;
            setTime(at);
          } else if (pendingRandomSeek.current) {
            // Anonymous preview: jump to a random spot (<2min in) so each clip sounds different
            pendingRandomSeek.current = false;
            applyRandomSeek();
          }
        }}
        onEnded={onEnded}
        onPlay={() => { setPlaying(true); window.dispatchEvent(new CustomEvent("wall:music-playing")); }}
        onPause={() => setPlaying(false)}
        data-testid="global-audio"
      />
    </PlayerContext.Provider>
  );
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const blobError = async (e) => {
  try {
    const d = e?.response?.data;
    if (d instanceof Blob) return JSON.parse(await d.text()).detail || "Download failed";
    return d?.detail || e.message || "Download failed";
  } catch { return "Download failed"; }
};
